import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "@/lib/axios";
import { useToast } from "@/components/ui/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Package,
  Plus,
  Trash2,
  Users2,
  Crown,
  MoreHorizontal,
  Pencil,
  RefreshCw,
  AlertTriangle,
  Loader2,
  Gift
} from "lucide-react";

// User interface'ini kaldırdık çünkü artık kullanıcı listesi özelliğini kaldırdık

// Tarih formatlama fonksiyonu - gün adıyla birlikte daha okunaklı format
const formatDateToReadable = (dateString: string | Date) => {
  const date = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = { 
    weekday: 'long', // Gün adı (Pazartesi, Salı, vb.)
    year: 'numeric', 
    month: 'long', // Ay adı (Ocak, Şubat, vb.)
    day: 'numeric' 
  };
  return date.toLocaleDateString('tr-TR', options);
};

const UserAccessControl = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<string>("user-access");

  // Artık kullanıcı listesi tablosunu kaldırdığımız için bu sorguya ihtiyaç yok

  // Paket atanmış firmaları getir
  const { data: companiesWithPackages, isLoading: isLoadingCompaniesWithPackages } = useQuery({
    queryKey: ["companies-with-packages"],
    queryFn: async () => {
      const { data } = await axios.get("/companies/with-packages");
      return data.data || [];
    },
    enabled: activeTab === "user-access"
  });
  
  // Sadece aktif paketli firmaları filtreleme fonksiyonu
  const getActivePackageCompanies = (companies: any[]) => {
    if (!companies || !Array.isArray(companies)) return [];
    return companies.filter(company => 
      company.announcementPackageDetails && 
      company.announcementPackageDetails.isActive === true && 
      company.activeAnnouncementPackage !== null
    );
  };
  
  // Artık ihtiyaç yok - companiesWithPackages'in yükleme durumunu kullanacağız
  
  // Eskiden kullanılan filtreleme fonksiyonu, artık kullanılmıyor
  // Filtreleme direk komponent içinde yapılıyor

  // Form state 
  const [newPackage, setNewPackage] = useState({
    name: "Pro Anons Paketi",
    packageType: "Pro",
    totalAnnouncements: 75,
    characterLimitPerAnnouncement: 500,
    price: 499,
    features: ["AI Anons Oluşturma", "Müzik Yükleme"],
    isActive: true,
    companyId: ""
  });
  const [editMode, setEditMode] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<any>(null);
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [isEditCompanyPackageDialogOpen, setIsEditCompanyPackageDialogOpen] = useState<boolean>(false);
  const [isRemovePackageDialogOpen, setIsRemovePackageDialogOpen] = useState<boolean>(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [selectedCompanyPackage, setSelectedCompanyPackage] = useState<any>(null);
  const [announcementBonusAmount, setAnnouncementBonusAmount] = useState(0);
  const [selectedPackageType, setSelectedPackageType] = useState("Pro");
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Form field değişikliklerini izleme
  const handleFormChange = (field: string, value: string | number | string[]) => {
    setNewPackage(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Firmaları alma sorgusu
  const { data: companies } = useQuery({
    queryKey: ["companies"],
    queryFn: async () => {
      const { data } = await axios.get("/companies");
      return data;
    }
  });

  // Paketleri alma sorgusu
  const { data: packages, isLoading: isLoadingPackages } = useQuery({
    queryKey: ["announcement-packages"],
    queryFn: async () => {
      const { data } = await axios.get("/ai-announcements/packages");
      return data || [];
    },
    enabled: activeTab === "package-management"
  });

  // Paket oluşturma
  const createPackage = useMutation({
    mutationFn: async (packageData: any) => {
      const response = await axios.post('/ai-announcements/packages', packageData);
      return response.data;
    },
    onSuccess: () => {
      toast({
        title: "Paket oluşturuldu",
        description: "Anons paketi başarıyla oluşturuldu.",
      });
      queryClient.invalidateQueries({ queryKey: ["announcement-packages"] });
      setIsDialogOpen(false);
      resetForm();
    },
    onError: (error: any) => {
      toast({
        title: "Hata",
        description: `Paket oluşturulamadı: ${error.message}`,
        variant: "destructive"
      });
    }
  });

  // Paket düzenleme için form yükleme
  const handleEditPackage = (pkg: any) => {
    setSelectedPackage(pkg);
    setNewPackage({
      name: pkg.name,
      packageType: pkg.packageType,
      price: pkg.price,
      totalAnnouncements: pkg.totalAnnouncements,
      characterLimitPerAnnouncement: pkg.characterLimitPerAnnouncement,
      features: pkg.features || [],
      isActive: pkg.isActive !== undefined ? pkg.isActive : true,
      companyId: pkg.company || ""
    });
    setEditMode(true);
    setIsDialogOpen(true);
  };

  // Paket atama için dialog açma
  const handleAssignPackage = (pkg: any) => {
    setSelectedPackage(pkg);
    setSelectedCompanyId("");
    setIsAssignDialogOpen(true);
  };
  
  // Paket talebini onaylama
  const approvePackageRequest = useMutation({
    mutationFn: async (companyId: string) => {
      const response = await axios.put(`/ai-announcements/packages/requests/${companyId}/approve`);
      return response.data;
    },
    onSuccess: () => {
      toast({
        title: "Talep onaylandı",
        description: "Paket satın alma talebi başarıyla onaylandı ve paket aktifleştirildi.",
      });
      // Tüm ilgili verileri yenile
      queryClient.invalidateQueries({ queryKey: ["pending-package-requests"] });
      queryClient.invalidateQueries({ queryKey: ["companies-with-packages"] });
      
      // Otomatik olarak 'Paket Atanmış Firmalar' sekmesine geç
      setActiveTab('assigned');
      
      // Sayfayı manuel olarak yenile - bu istek üzerine eklendi
      setTimeout(() => {
        window.location.reload();
      }, 1000); // 1 saniye sonra sayfayı yenile
    },
    onError: (error: any) => {
      toast({
        title: "Hata",
        description: `Talep onaylanamadı: ${error.message}`,
        variant: "destructive"
      });
    }
  });
  
  // Paket talebini reddetme
  const rejectPackageRequest = useMutation({
    mutationFn: async ({ companyId, rejectReason }: { companyId: string, rejectReason: string }) => {
      const response = await axios.put(`/ai-announcements/packages/requests/${companyId}/reject`, { rejectReason });
      return response.data;
    },
    onSuccess: () => {
      toast({
        title: "Talep reddedildi",
        description: "Paket satın alma talebi reddedildi.",
      });
      // Tüm ilgili verileri yenile
      queryClient.invalidateQueries({ queryKey: ["pending-package-requests"] });
      queryClient.invalidateQueries({ queryKey: ["companies-with-packages"] });
      
      // Sayfayı manuel olarak yenile - bu istek üzerine eklendi
      setTimeout(() => {
        window.location.reload();
      }, 1000); // 1 saniye sonra sayfayı yenile
    },
    onError: (error: any) => {
      toast({
        title: "Hata",
        description: `Talep reddedilemedi: ${error.message}`,
        variant: "destructive"
      });
    }
  });

  // Paketi firmaya atama
  const assignPackage = useMutation({
    mutationFn: async ({ packageId, companyId }: { packageId: string, companyId: string }) => {
      const response = await axios.post(`/ai-announcements/packages/${packageId}/assign-to-company`, { companyId });
      return response.data;
    },
    onSuccess: () => {
      toast({
        title: "Paket atandı",
        description: "Anons paketi başarıyla firmaya atandı.",
      });
      queryClient.invalidateQueries({ queryKey: ["announcement-packages"] });
      setIsAssignDialogOpen(false);
      setSelectedPackage(null);
    },
    onError: (error: any) => {
      toast({
        title: "Hata",
        description: `Paket firmaya atanamadı: ${error.message}`,
        variant: "destructive"
      });
    }
  });

  // Firma seçimini tamamla ve paketi ata
  const handleAssignToCompany = () => {
    if (!selectedCompanyId || !selectedPackage) {
      toast({
        title: "Hata",
        description: "Lütfen bir firma seçin",
        variant: "destructive"
      });
      return;
    }

    assignPackage.mutate({
      packageId: selectedPackage._id || selectedPackage.id,
      companyId: selectedCompanyId
    });
  };
  
  // Paket tipi tanımlaması
  interface PackageType {
    _id: string;
    id?: string;
    name: string;
    totalAnnouncements: number;
    characterLimitPerAnnouncement: number;
    price: number;
    packageType: string;
    isActive: boolean;
    features: string[];
    suggestedCompanyId?: string;
    company?: any;
  }

  // Paket silme işlemi için state ve dialog tanımları
  const [isDeletePackageDialogOpen, setIsDeletePackageDialogOpen] = useState(false);
  const [selectedPackageToDelete, setSelectedPackageToDelete] = useState<PackageType | null>(null);
  
  // Paket kalıcı olarak silme mutasyonu
  const deletePackage = useMutation({
    mutationFn: async (packageId: string) => {
      const response = await axios.delete(`/ai-announcements/packages/${packageId}`);
      return response.data;
    },
    onSuccess: (data) => {
      toast({
        title: "Paket silindi",
        description: `Paket başarıyla silindi. Etkilenen firma sayısı: ${data.affectedCompanies}`,
      });
      setIsDeletePackageDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ["announcement-packages"] });
      queryClient.invalidateQueries({ queryKey: ["companies-with-packages"] });
    },
    onError: (error: any) => {
      toast({
        title: "Hata",
        description: `Paket silinemedi: ${error.message}`,
        variant: "destructive"
      });
    }
  });

 

  // Firma paketine ekstra anons hakkı ekle ve paket tipini güncelle
  // Firma paketini kaldırma/iptal etme mutasyonu
  const removeCompanyPackage = useMutation({
    mutationFn: async (companyId: string) => {
      const response = await axios.delete(`/companies/${companyId}/remove-package`);
      return response.data;
    },
    onSuccess: () => {
      toast({
        title: "Paket kaldırıldı",
        description: "Firmanın anons paketi başarıyla kaldırıldı.",
      });
      queryClient.invalidateQueries({ queryKey: ["companies-with-packages"] });
    },
    onError: (error: any) => {
      toast({
        title: "Hata",
        description: `Paket kaldırılamadı: ${error.message}`,
        variant: "destructive"
      });
    }
  });

  const addAnnouncementCredit = useMutation({
    mutationFn: async ({ companyId, announcementAmount, packageId }: { companyId: string, announcementAmount: number, packageId: string }) => {
      const response = await axios.post(`/companies/${companyId}/add-announcement-credit`, { 
        announcementAmount, 
        packageId  // Paket ID'sini gönderelim
      });
      return response.data;
    },
    onSuccess: () => {
      toast({
        title: "Haklar eklendi",
        description: "Anons hakları başarıyla eklendi.",
      });
      queryClient.invalidateQueries({ queryKey: ["companies-with-packages"] });
      setIsEditCompanyPackageDialogOpen(false);
    },
    onError: (error: any) => {
      toast({
        title: "Hata",
        description: `Anons hakları eklenemedi: ${error.message}`,
        variant: "destructive"
      });
    }
  });

  // Paket düzenleme işlemini tamamla
  const handleSubmitPackageEdit = () => {
    if (!selectedCompanyPackage) {
      toast({
        title: "Hata",
        description: "Firma seçilmedi",
        variant: "destructive"
      });
      return;
    }

    console.log("Güncelleme yapılıyor:", {
      companyId: selectedCompanyPackage._id,
      announcementAmount: announcementBonusAmount || 0,
      packageId: selectedPackageType
    });

    addAnnouncementCredit.mutate({
      companyId: selectedCompanyPackage._id,
      announcementAmount: announcementBonusAmount || 0,
      packageId: selectedPackageType  // Paket türü yerine paket ID'sini gönderiyoruz
    });
  };

  // Paket düzenleme işlemi
  const updatePackage = useMutation({
    mutationFn: async ({ packageId, data }: { packageId: string, data: any }) => {
      const response = await axios.put(`/ai-announcements/packages/${packageId}`, data);
      return response.data;
    },
    onSuccess: () => {
      toast({
        title: "Paket güncellendi",
        description: "Anons paketi başarıyla güncellendi.",
      });
      queryClient.invalidateQueries({ queryKey: ["announcement-packages"] });
      setIsDialogOpen(false);
      resetForm();
    },
    onError: (error: any) => {
      toast({
        title: "Hata",
        description: `Paket güncellenemedi: ${error.message}`,
        variant: "destructive"
      });
    }
  });

  // Form değerlerini sıfırla
  const resetForm = () => {
    setEditMode(false);
    setSelectedPackage(null);
    setNewPackage({
      name: "Pro Anons Paketi",
      packageType: "Pro",
      totalAnnouncements: 75,
      characterLimitPerAnnouncement: 500,
      price: 499,
      features: ["AI Anons Oluşturma", "Müzik Yükleme"],
      isActive: true,
      companyId: ""
    });
  };

  // Paketi sunucuya gönder (oluşturma veya güncelleme)
  const handleCreatePackage = () => {
    // Paket verisi oluştur
    const packageData: any = {...newPackage};
    
    // Firma seçimi "none" ise veya yoksa, companyId alanını API'ye göndermeden önce kaldır
    if (!packageData.companyId || packageData.companyId === "none") {
      delete packageData.companyId;
    }
    
    // Eğer düzenleme modu aktifse, güncelleme işlemi yap
    if (editMode && selectedPackage) {
      updatePackage.mutate({
        packageId: selectedPackage._id || selectedPackage.id,
        data: packageData
      });
    } else {
      // Yeni paket oluştur
      createPackage.mutate(packageData);
    }
  };
  
  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Firma Paketi Düzenleme Dialog */}
      <Dialog open={isEditCompanyPackageDialogOpen} onOpenChange={setIsEditCompanyPackageDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Paket Haklarını Düzenle</DialogTitle>
            <DialogDescription>
              {selectedCompanyPackage?.name} firmasının anons haklarını düzenleyin
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-muted/50 p-3 rounded-md space-y-1 mb-4">
              <div className="flex justify-between">
                <span>Firma:</span>
                <span className="font-medium">{selectedCompanyPackage?.name}</span>
              </div>
              <div className="flex justify-between">
                <span>Mevcut Haklar:</span>
                <span className="font-medium">
                  {selectedCompanyPackage?.announcementPackageDetails?.remainingAnnouncements} / {selectedCompanyPackage?.announcementPackageDetails?.totalAnnouncements}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Bitiş Tarihi:</span>
                <span className="font-medium">
                  {selectedCompanyPackage?.announcementPackageDetails?.expiryDate ? 
                    formatDateToReadable(selectedCompanyPackage.announcementPackageDetails.expiryDate) : '-'}
                </span>
              </div>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="packageType">Paket Türü</Label>
                <Select 
                  value={selectedPackageType}
                  onValueChange={setSelectedPackageType}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Paket türü seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {packages?.map((pkg: any) => (
                      <SelectItem key={pkg._id || pkg.id} value={pkg._id || pkg.id}>
                        {pkg.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="announcementBonus">Eklenecek Ekstra Anons Hakkı</Label>
                <Input 
                  id="announcementBonus" 
                  type="number" 
                  placeholder="Örn: 10"
                  min="0"
                  value={announcementBonusAmount || ''} 
                  onChange={(e) => setAnnouncementBonusAmount(parseInt(e.target.value) || 0)}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditCompanyPackageDialogOpen(false)}>İptal</Button>
            <Button 
              onClick={handleSubmitPackageEdit}
              disabled={addAnnouncementCredit.isPending}
            >
              {addAnnouncementCredit.isPending ? "Ekleniyor..." : "Hakları Ekle"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Paket Kaldırma Onay Dialog'u */}
      <Dialog open={isRemovePackageDialogOpen} onOpenChange={setIsRemovePackageDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Paketi Kaldır</DialogTitle>
            <DialogDescription>
              Bu işlem geri alınamaz. Devam etmek istediğinize emin misiniz?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedCompanyPackage && (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <div className="font-medium">Firma Adı:</div>
                  <div>
                    {typeof selectedCompanyPackage.company === "string"
                      ? companies?.find((c: any) => c._id === selectedCompanyPackage.company)?.name
                      : selectedCompanyPackage.company?.name}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="font-medium">Paket Adı:</div>
                  <div>{selectedCompanyPackage.name}</div>
                </div>
              </>
            )}
            <div className="text-sm text-destructive">
              <AlertTriangle className="h-4 w-4 inline-block mr-1" />
              Paketi kaldırdığınızda, firma tüm anons haklarını kaybedecek ve AI anons erişimi devre dışı bırakılacaktır.
            </div>
          </div>
          <DialogFooter className="flex justify-between">
            <Button variant="outline" onClick={() => setIsRemovePackageDialogOpen(false)}>
              İptal
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => {
                if (selectedCompanyId) {
                  removeCompanyPackage.mutate(selectedCompanyId, {
                    onSuccess: () => {
                      setIsRemovePackageDialogOpen(false);
                      setSelectedCompanyId("");
                      setSelectedCompanyPackage(null);
                    }
                  });
                }
              }}
              disabled={removeCompanyPackage.isPending}
            >
              {removeCompanyPackage.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Kaldırılıyor...
                </>
              ) : (
                "Paketi Kaldır"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">AI Anons Yönetimi</h2>
          <p className="text-muted-foreground">
            Kullanıcı erişimlerini ve anons paketlerini yönetin
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Crown className="h-5 w-5 text-yellow-500" />
          <span>Süper Admin Kontrol Paneli</span>
        </div>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="user-access">
            <Users2 className="h-4 w-4 mr-2" />
            Paket Atanmış Firmalar
          </TabsTrigger>
          <TabsTrigger value="pending-requests">
            <AlertTriangle className="h-4 w-4 mr-2" />
            Onay Bekleyen Talepler
          </TabsTrigger>
          <TabsTrigger value="package-management">
            <Package className="h-4 w-4 mr-2" />
            Paket Yönetimi
          </TabsTrigger>
        </TabsList>
        
        {/* Onay Bekleyen Talepler Sekmesi */}
        <TabsContent value="pending-requests">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="flex items-center">
                    <AlertTriangle className="h-5 w-5 text-amber-500 mr-2" />
                    Onay Bekleyen Paket Talepleri
                  </CardTitle>
                  <CardDescription>
                    Kullanıcıların satın alma taleplerini onaylayın veya reddedin
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => queryClient.invalidateQueries({ queryKey: ["pending-package-requests"] })}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Yenile
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {isLoadingCompaniesWithPackages ? (
                <div className="flex justify-center items-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  <span className="ml-3 text-muted-foreground">Bekleyen talepler yükleniyor...</span>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Firma Adı</TableHead>
                      <TableHead>Paket Tipi</TableHead>
                      <TableHead>Paket Adı</TableHead>
                      <TableHead>Anons Hakkı</TableHead>
                      <TableHead>Talep Tarihi</TableHead>
                      <TableHead>Durum</TableHead>
                      <TableHead>İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {companiesWithPackages?.length > 0 ? (
                      companiesWithPackages.filter((company: any) => {
                        // Debug için filtreleme koşullarını loglayalım
                        if (company.announcementPackageDetails) {
                          console.log(`Firma ${company.name} talep kontrol:`, {
                            isPendingApproval: company.announcementPackageDetails.isPendingApproval,
                            status: company.announcementPackageDetails.status,
                            isActive: company.announcementPackageDetails.isActive,
                            isRequest: company.announcementPackageDetails.isRequest,
                            hasActivePackage: !!company.activeAnnouncementPackage
                          });
                        }
                        
                        // Filtreleme koşulu: isPendingApproval=true VEYA status=pending VEYA 
                        // (isActive=false VE activeAnnouncementPackage=null VE isRequest=true)
                        return company.announcementPackageDetails && (
                          company.announcementPackageDetails.isPendingApproval === true || 
                          company.announcementPackageDetails.status === 'pending' ||
                          (company.announcementPackageDetails.isActive === false && 
                           !company.activeAnnouncementPackage &&
                           company.announcementPackageDetails.isRequest === true)
                        );
                      }).map((company: any) => (
                        <TableRow key={company._id}>
                          <TableCell className="font-medium">{company.name}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{company.announcementPackageDetails.packageType}</Badge>
                          </TableCell>
                          <TableCell>{company.announcementPackageDetails.name || company.announcementPackageDetails.packageName}</TableCell>
                          <TableCell>
                            <Badge variant="secondary">
                              {company.announcementPackageDetails.totalAnnouncements}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {formatDateToReadable(company.announcementPackageDetails.requestDate)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Badge variant="warning" className="bg-amber-500">
                                Onay Bekliyor
                              </Badge>
                              {company.announcementPackageDetails.price && (
                                <Badge variant="outline" className="ml-1">
                                  {company.announcementPackageDetails.price}₺
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center space-x-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="bg-green-50 text-green-600 border-green-200 hover:bg-green-100 hover:text-green-700"
                                onClick={() => approvePackageRequest.mutate(company._id)}
                              >
                                Onayla
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="bg-red-50 text-red-600 border-red-200 hover:bg-red-100 hover:text-red-700"
                                onClick={() => {
                                  // Burada red sebebi için bir dialog açılabilir
                                  if (window.confirm('Bu talebi reddetmek istediğinize emin misiniz?')) {
                                    rejectPackageRequest.mutate({ 
                                      companyId: company._id, 
                                      rejectReason: 'Süper admin tarafından reddedildi.' 
                                    });
                                  }
                                }}
                              >
                                Reddet
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8">
                          <div className="flex flex-col items-center justify-center text-muted-foreground">
                            <Gift className="h-12 w-12 mb-2 text-muted-foreground/70" />
                            <p className="text-lg font-medium">Bekleyen paket talebi yok</p>
                            <p className="text-sm">Tüm talepler değerlendirilmiş görünüyor</p>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="user-access" className="space-y-6">
            <div className="grid grid-cols-1 gap-6">
              {/* Paket Atanmış Firmalar Listesi */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle>Paket Atanmış Firmalar</CardTitle>
                      <CardDescription>
                        AI Anons paketi atanmış firmaların listesi ve detayları
                      </CardDescription>
                    </div>
                    <Button 
                      variant="outline" 
                      onClick={() => queryClient.invalidateQueries({ queryKey: ["companies-with-packages"] })}
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Yenile
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {isLoadingCompaniesWithPackages ? (
                    <div className="text-center py-4">Firmalar yükleniyor...</div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Firma Adı</TableHead>
                          <TableHead>Şehir</TableHead>
                          <TableHead>Paket Tipi</TableHead>
                          <TableHead>Anons Hakkı</TableHead>
                          <TableHead>Atanma Tarihi</TableHead>
                          <TableHead>Bitiş Tarihi</TableHead>
                          <TableHead>Durum</TableHead>
                          <TableHead>İşlemler</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {companiesWithPackages?.length > 0 ? (
                          getActivePackageCompanies(companiesWithPackages).map((company: any) => (
                            <TableRow key={company._id}>
                              <TableCell className="font-medium">{company.name}</TableCell>
                              <TableCell>{company.city}</TableCell>
                              <TableCell>
                                <Badge variant="outline">{company.announcementPackageDetails.packageType}</Badge>
                              </TableCell>
                              <TableCell>
                                <Badge variant="secondary">
                                  {company.announcementPackageDetails.remainingAnnouncements} / {company.announcementPackageDetails.totalAnnouncements}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                {formatDateToReadable(company.announcementPackageDetails.assignedAt)}
                              </TableCell>
                              <TableCell>
                                {formatDateToReadable(company.announcementPackageDetails.expiryDate)}
                              </TableCell>
                              <TableCell>
                                <Badge variant={company.announcementPackageDetails.isActive ? "success" : "destructive"}>
                                  {company.announcementPackageDetails.isActive ? "Aktif" : "Pasif"}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="outline" size="sm">
                                      <MoreHorizontal className="h-4 w-4" />
                                      İşlemler
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem 
                                      className="cursor-pointer" 
                                      onSelect={() => {
                                        console.log("Düzenle seçildi", company);
                                        // Dialog'u aç
                                        setSelectedCompanyPackage(company);
                                        setAnnouncementBonusAmount(0);
                                        setSelectedPackageType(company.announcementPackageDetails.packageType || "Pro");
                                        setTimeout(() => {
                                          setIsEditCompanyPackageDialogOpen(true);
                                        }, 100);
                                      }}
                                    >
                                      <Pencil className="h-4 w-4 mr-2" />
                                      Düzenle
                                    </DropdownMenuItem>
                                    <DropdownMenuItem 
                                      className="cursor-pointer text-destructive" 
                                      onSelect={() => {
                                        // Paketi kaldır işlemi
                                        setSelectedCompanyId(company._id);
                                        setSelectedCompanyPackage(company);
                                        setIsRemovePackageDialogOpen(true);
                                      }}
                                    >
                                      <Trash2 className="h-4 w-4 mr-2" />
                                      Paketi Kaldır
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={8} className="text-center py-4">
                              <div className="flex flex-col items-center justify-center text-muted-foreground">
                                <Package className="h-12 w-12 mb-2" />
                                <p>Paket atanmış firma bulunmuyor</p>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        
        <TabsContent value="package-management">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Anons Paketi Yönetimi</CardTitle>
                  <CardDescription>
                    Firmalara özel anons paketlerini yönetin
                  </CardDescription>
                </div>
                <Dialog open={isDialogOpen} onOpenChange={(open) => {
                  setIsDialogOpen(open);
                  if (!open) resetForm();
                }}>
                  <DialogTrigger asChild>
                    <Button>
                      <Plus className="w-4 h-4 mr-2" />
                      Yeni Paket Oluştur
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle>{editMode ? 'Anons Paketi Düzenle' : 'Yeni Anons Paketi'}</DialogTitle>
                      <DialogDescription>
                        {editMode ? 'Mevcut anons paketini düzenleyin' : 'Yeni bir anons paketi şablonu oluşturun'}
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="packageName">Paket Adı</Label>
                        <Input 
                          id="packageName"
                          value={newPackage.name} 
                          onChange={(e) => handleFormChange("name", e.target.value)}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="packageType">Paket Tipi</Label>
                          <Select 
                            value={newPackage.packageType}
                            onValueChange={(value) => handleFormChange("packageType", value)}
                          >
                            <SelectTrigger id="packageType">
                              <SelectValue placeholder="Paket tipi seçin" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Basic">Basic</SelectItem>
                              <SelectItem value="Pro">Pro</SelectItem>
                              <SelectItem value="Premium">Premium</SelectItem>
                              <SelectItem value="Custom">Custom</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor="price">Fiyat (₺)</Label>
                          <Input 
                            id="price" 
                            type="number" 
                            value={newPackage.price}
                            onChange={(e) => handleFormChange("price", parseInt(e.target.value))}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="totalAnnouncements">Toplam Anons Hakkı</Label>
                          <Input 
                            id="totalAnnouncements" 
                            type="number" 
                            value={newPackage.totalAnnouncements}
                            onChange={(e) => handleFormChange("totalAnnouncements", parseInt(e.target.value))}
                          />
                        </div>
                        <div>
                          <Label htmlFor="characterLimit">Karakter Limiti</Label>
                          <Input 
                            id="characterLimit" 
                            type="number" 
                            value={newPackage.characterLimitPerAnnouncement}
                            onChange={(e) => handleFormChange("characterLimitPerAnnouncement", parseInt(e.target.value))}
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="features">Özellikler (virgülle ayırın)</Label>
                        <Input 
                          id="features" 
                          value={newPackage.features.join(", ")} 
                          onChange={(e) => handleFormChange("features", e.target.value.split(", ").map(feature => feature.trim()))} 
                          placeholder="AI Anons, Arka Plan Müziği, Ses Ayarları"
                        />
                      </div>
                      <div>
                        <Label htmlFor="companySelect">Özel Teklif Gösterilecek Firma (Opsiyonel)</Label>
                        <Select 
                          value={newPackage.companyId}
                          onValueChange={(value) => handleFormChange("companyId", value)}
                        >
                          <SelectTrigger id="companySelect">
                            <SelectValue placeholder="Firma seçin (opsiyonel)" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Genel Paket (Tüm Firmalar)</SelectItem>
                            {companies?.map((company: any) => (
                              <SelectItem key={company._id} value={company._id}>
                                {company.name} için özel teklif
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground mt-1">Paket listesinde "Size Özel Teklif" olarak görünecek firma</p>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setIsDialogOpen(false)}>İptal</Button>
                      <Button 
                        onClick={handleCreatePackage}
                        disabled={(createPackage.isPending || updatePackage.isPending) || !newPackage.name?.trim()}
                      >
                        {createPackage.isPending || updatePackage.isPending ? "Kaydediliyor..." : editMode ? "Güncelle" : "Paketi Oluştur"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                {/* Firmaya Atama Dialog */}
                {/* Paket Silme Dialog */}
                <Dialog open={isDeletePackageDialogOpen} onOpenChange={setIsDeletePackageDialogOpen}>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle className="text-red-600">Paketi Tamamen Sil</DialogTitle>
                      <DialogDescription>
                        <div className="mt-2 mb-2">
                          <AlertTriangle className="h-6 w-6 text-yellow-500 mx-auto mb-2" />
                          <p>Dikkat! Bu işlem geri alınamaz. Paket kalıcı olarak silinecek ve bu paketi kullanan tüm firmaların erişimi kaldırılacaktır.</p>
                        </div>
                        <div className="bg-gray-100 p-3 rounded my-2">
                          <p className="font-medium">{selectedPackageToDelete?.name}</p>
                          <p className="text-sm text-gray-600">{selectedPackageToDelete?.totalAnnouncements} anons hakkı, {selectedPackageToDelete?.characterLimitPerAnnouncement} karakter</p>
                        </div>
                      </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="mt-4">
                      <Button variant="outline" onClick={() => setIsDeletePackageDialogOpen(false)}>Vazgeç</Button>
                      <Button 
                        variant="destructive" 
                        onClick={() => {
                          if (selectedPackageToDelete?._id) {
                            deletePackage.mutate(selectedPackageToDelete._id);
                          }
                        }}
                        disabled={deletePackage.isPending}
                      >
                        {deletePackage.isPending ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Paket Siliniyor...
                          </>
                        ) : (
                          <>Paketi Tamamen Sil</>
                        )}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
                
                {/* Paket Atama Dialog */}
                <Dialog open={isAssignDialogOpen} onOpenChange={setIsAssignDialogOpen}>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Paketi Firmaya Ata</DialogTitle>
                      <DialogDescription>
                        {selectedPackage?.name} paketini bir firmaya atayın. Paket 30 gün süreyle aktif olacaktır.
                      </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label htmlFor="companySelect">Firma Seçin</Label>
                        <Select
                          value={selectedCompanyId}
                          onValueChange={setSelectedCompanyId}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Bir firma seçin" />
                          </SelectTrigger>
                          <SelectContent>
                            {companies?.map((company: any) => (
                              <SelectItem key={company._id || company.id} value={company._id || company.id}>
                                {company.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="bg-muted/50 p-3 rounded-md space-y-1">
                        <div className="flex justify-between">
                          <span>Toplam Anons Hakkı:</span>
                          <span className="font-medium">{selectedPackage?.totalAnnouncements}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Karakter Limiti:</span>
                          <span className="font-medium">{selectedPackage?.characterLimitPerAnnouncement}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Geçerlilik Süresi:</span>
                          <span className="font-medium">30 gün</span>
                        </div>
                      </div>
                    </div>

                    <DialogFooter>
                      <Button variant="outline" onClick={() => setIsAssignDialogOpen(false)}>Vazgeç</Button>
                      <Button 
                        onClick={handleAssignToCompany}
                        disabled={assignPackage.isPending || !selectedCompanyId}
                      >
                        {assignPackage.isPending ? "Atanıyor..." : "Firmaya Ata"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>


              </div>
            </CardHeader>
            <CardContent>
              {isLoadingPackages ? (
                <div className="text-center py-4">Paketler yükleniyor...</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Paket Adı</TableHead>
                      <TableHead>Firma</TableHead>
                      <TableHead>Anons Hakkı</TableHead>
                      <TableHead>Karakter Limiti</TableHead>
                      <TableHead>Fiyat</TableHead>
                      <TableHead>Durum</TableHead>
                      <TableHead className="text-right">İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {packages?.length > 0 ? (
                      packages.map((pkg: any) => (
                        <TableRow key={pkg._id || pkg.id}>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              {pkg.name}
                              {pkg.suggestedCompanyId && (
                                <Badge variant="secondary" className="ml-2">
                                  <Gift className="h-3 w-3 mr-1" /> Özel Teklif
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {pkg.suggestedCompanyId ? (
                              <div className="flex items-center">
                                {companies?.find((c: any) => c._id === pkg.suggestedCompanyId)?.name || "Belirtilen Firma"}
                                <Badge variant="outline" className="ml-2 bg-green-50">
                                  <Gift className="h-3 w-3 mr-1 text-green-600" /> Özel
                                </Badge>
                              </div>
                            ) : (
                              <Badge variant="outline">
                                Genel Paket
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {pkg.remainingAnnouncements} / {pkg.totalAnnouncements}
                            </Badge>
                          </TableCell>
                          <TableCell>{pkg.characterLimitPerAnnouncement}</TableCell>
                          <TableCell>{pkg.price ? `${pkg.price} ₺` : '-'}</TableCell>
                          <TableCell>
                            <Badge variant={pkg.isActive ? "default" : "destructive"}>
                              {pkg.isActive ? "Aktif" : "Deaktif"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem className="cursor-pointer" onSelect={() => handleEditPackage(pkg)}>
                                  <Pencil className="h-4 w-4 mr-2" />
                                  Düzenle
                                </DropdownMenuItem>
                                <DropdownMenuItem className="cursor-pointer" onSelect={() => handleAssignPackage(pkg)}>
                                  <Users2 className="h-4 w-4 mr-2" />
                                  Firmaya Ata
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  className="cursor-pointer text-destructive" 
                                  onSelect={() => {
                                    // Paketi silme işlemi
                                    setSelectedPackageToDelete(pkg);
                                    setIsDeletePackageDialogOpen(true);
                                  }}
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Paketi Kaldır
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-4">
                          <div className="flex flex-col items-center justify-center text-muted-foreground">
                            <Package className="h-12 w-12 mb-2" />
                            <p>Henüz anons paketi bulunmuyor</p>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};


export default UserAccessControl;
